/* eslint-disable no-unsafe-optional-chaining */
import {
  ActivityHeader,
  Breadcrumbs,
  Icon,
  Modal,
  ModuleActionBar,
  ProgressBar,
  ReportModalLayout,
} from '@components';
import { routes } from '@constants';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import Net from '@pngs/TestPage/Net10.png';
import { activityApi, userApi } from '@store';
import { Button, Flex, theme, Typography } from '@styles';
import { ReactComponent as EyeIcon } from '@svgs/visibility.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { findCheckPoint } from 'src/constants/rankPoints';

import { testPageLayoutByVariant } from '../TestPage/TestPage.layoutProps';
import { FinishedCard, ScenarioCard } from './components';
import { Container } from './ScenarioPage.layoutProps';

const ScenarioPage = () => {
  const { i18n } = useTranslation();
  const { navigate } = useNavigateWithOfflineMode();
  const location = useLocation();
  const { device, isLaptop, isTabletSM } = useScreenSize();
  const layout = testPageLayoutByVariant[device || 'desktop'];

  const [shouldShowTitleCard, setShouldShowTitleCard] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [isReportModalVisible, setReportModalVisible] = useState(false);
  const [validatedAnswers, setValidatedAnswers] = useState(null);

  const { data: scenario, refetch: refetchScenario } =
    activityApi.useGetScenarioByIdQuery({
      id: location.state.exerciseId,
    });

  useEffect(() => {
    refetchScenario();
  }, [i18n.language]);

  const handleBeginScenario = () => {
    setShouldShowTitleCard(false);
  };

  const handleNextButtonClick = () => {
    if (currentStep === scenario?.steps.length) return;

    setCurrentStep(currentStep + 1);
  };

  const handleFinishButtonPress = () => {
    navigate(routes.activities);
  };

  const handleTryAgainButtonClick = () => {
    setCurrentStep(0);
    setShouldShowTitleCard(true);
  };

  const handleReportButtonClick = () => {
    setReportModalVisible(true);
  };

  const isTestFinished = currentStep === scenario?.steps.length;
  const [isRankPointsModalVisible, setRankPointsModalVisible] = useState(false);
  const handleRankButtonClick = () => {
    setRankPointsModalVisible(true);
  };
  const { data: userData } = userApi.useGetUserQuery();
  const rankCheckPoint = useMemo(
    () =>
      userData?.data?.rank_points &&
      findCheckPoint(userData?.data?.rank_points),
    [userData?.rank_points]
  );

  return (
    <Container
      backgroundImage={Net}
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      flexDirection="column"
    >
      <Modal
        isVisible={isRankPointsModalVisible}
        modalContainerProps={{
          backgroundColor: theme.colors.white[10],
          border: `1px solid ${theme.colors.white[10]}`,
          borderRadius: '0px',
          maxHeight: isTabletSM ? '100%' : '480px',
          maxWidth: '640px',
          width: '100%',
          zIndex: 100,
        }}
        setModalVisible={setRankPointsModalVisible}
        withBackdropNoise
        // {...(isTabletSM && { modalMaxHeight: '100%' })}
      >
        <Flex flexDirection="column" p={40} width="100%">
          <Typography
            color={theme.colors.white[100]}
            textAlign="center"
            variant="MS-bold-s28-lh29"
          >
            {`${userData?.data?.rank_points}/${rankCheckPoint} `}
            {i18n.t('MainPage.PointsToNextRank').toUpperCase()}
          </Typography>
          <Flex border="3px solid rgb(255, 255, 255, 0.2)" my={24} width="100%">
            <Flex
              background={theme.colors.schoolBusYellow[100]}
              height="12px"
              m="2px"
              width={`${(100 * userData?.data?.rank_points) / rankCheckPoint}%`}
            />
          </Flex>
          <Flex
            background="rgb(255, 255, 255, 0.2)"
            height="2px"
            my={24}
            width="100%"
          />
          <Button
            onClick={() => setRankPointsModalVisible(false)}
            variant="secondary"
          >
            <Typography variant="MS-regular-s16-lh25">
              {i18n.t('ActivityModal.Close')}
            </Typography>
          </Button>
        </Flex>
      </Modal>
      <ReportModalLayout
        activityId={location.state.exerciseId}
        isLessonActivity={false}
        isVisible={isReportModalVisible}
        setIsVisible={setReportModalVisible}
        withBackdropNoise
      />
      <Breadcrumbs containerProps={{ px: layout?.px }} />
      {shouldShowTitleCard ? (
        <Flex
          alignItems="center"
          flexDirection={isTabletSM ? 'column' : 'row'}
          gap={8}
          justifyContent="space-between"
          pb={80}
          pl={layout?.pl}
          pr={isTabletSM ? layout?.pl : 0}
          pt={isTabletSM ? 0 : 80}
        >
          {isTabletSM && (
            <Flex
              backgroundImage={scenario?.image}
              // eslint-disable-next-line no-nested-ternary
              height={isTabletSM ? 248 : isLaptop ? 337 : 470}
              maxWidth={isLaptop ? 523 : 650}
              width="100%"
            />
          )}
          <Flex
            alignItems="flex-start"
            flex={1}
            flexDirection="column"
            mr={isTabletSM ? 0 : 40}
            {...(isTabletSM && { width: '100%' })}
          >
            <Typography
              color={theme.colors.white[50]}
              mb={isTabletSM ? '8px' : 32}
              variant={isTabletSM ? 'BN-regular-s36-lh43' : 'BN-bold-s43-lh38'}
            >
              {i18n.t('ScenarioPage.Scenario').toUpperCase()}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              maxWidth={566}
              mb={10}
              variant={isTabletSM ? 'BN-bold-s32-lh28' : 'BN-bold-s64-lh64'}
              width="100%"
            >
              {scenario?.title?.toUpperCase()}
            </Typography>
            <Flex alignItems="center" gap={8} mb={30}>
              <Icon color={theme.colors.alto[100]} SVG={EyeIcon} />
              <Typography
                color={theme.colors.alto[100]}
                variant="MS-regular-s14-lh22"
              >
                {scenario?.views}
              </Typography>
            </Flex>
            <Button
              onClick={handleBeginScenario}
              variant="primary"
              whiteSpace="nowrap"
              width={340}
            >
              <Typography
                color={theme.colors.black[100]}
                variant="MS-semibold-s16-lh17"
              >
                {i18n.t('ScenarioPage.RunScenario').toUpperCase()}
              </Typography>
            </Button>
          </Flex>
          {!isTabletSM && (
            <Flex
              backgroundImage={scenario?.image}
              height={isLaptop ? 337 : 470}
              maxWidth={isLaptop ? 523 : 650}
              width="100%"
            />
          )}
        </Flex>
      ) : (
        <Flex flexDirection="column" px={layout?.px}>
          <ModuleActionBar
            additionalButton={{ isVisible: false }}
            progress={{ isSemivisible: true, percentage: 0 }}
            rank={{
              data: userData?.data?.rank ? { ...userData?.data?.rank } : null,
              isVisible: true,
              onClick: handleRankButtonClick,
              variant: 'rank',
            }}
            report={{
              isVisible: true,
              onClick: handleReportButtonClick,
              variant: 'report',
            }}
            sidePanel={{ isSemivisible: true }}
          />
          <ActivityHeader
            activityVariant="scenario"
            containerProps={{ mb: 20, mt: 24 }}
            title={scenario?.title}
          />
          <ProgressBar
            currentStep={currentStep}
            items={isTestFinished ? validatedAnswers : scenario?.steps}
            withCorrection={isTestFinished}
          />
          {isTestFinished ? (
            <FinishedCard
              correctAnswersCount={
                validatedAnswers.filter((answer) => answer.is_correct).length
              }
              onFinishButtonPress={handleFinishButtonPress}
              onTryAgainButtonPress={handleTryAgainButtonClick}
              questionsCount={scenario?.steps.length}
            />
          ) : (
            <ScenarioCard
              cardContent={scenario.steps[currentStep]}
              mb={80}
              mt={24}
              onNextButtonClick={handleNextButtonClick}
              setValidatedAnswers={setValidatedAnswers}
            />
          )}
        </Flex>
      )}
    </Container>
  );
};

export default ScenarioPage;
